/**
 * Migration hooks — TanStack Query wrappers around the migration API.
 *
 * - `useMigrationStatus()` is fired once on Dashboard mount and cached
 *   for the lifetime of the session (`staleTime: Infinity`, `retry: false`).
 *   A 404 from older backends that don't know about v2 migration is
 *   treated as "no legacy data" by disabling retries — the consumer
 *   checks `isError` separately and shows the normal dashboard.
 * - `useMigrateLegacy()` runs the actual import and invalidates both
 *   the agents list and the migration-status cache on success so the
 *   UI reflects the new state without a manual refetch.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getMigrationStatus,
  migrateLegacy,
  type MigrationResult,
  type MigrationStatus,
} from '@/api/migration';

/**
 * Reads `/api/system/migration-status`. Cached forever so we don't spam
 * the server. Disabled retries so that old backends returning 404 don't
 * log errors on every screen.
 */
export function useMigrationStatus() {
  return useQuery<MigrationStatus>({
    queryKey: ['migration-status'],
    queryFn: getMigrationStatus,
    staleTime: Infinity,
    retry: false,
  });
}

/** Mutation hook for `POST /api/system/migrate-legacy`. */
export function useMigrateLegacy() {
  const qc = useQueryClient();
  return useMutation<MigrationResult>({
    mutationFn: migrateLegacy,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['agents'] });
      qc.invalidateQueries({ queryKey: ['migration-status'] });
    },
  });
}
